import type { Column } from "@tanstack/react-table";
import { Check, PlusCircle, XCircle } from "lucide-react";
import * as React from "react";
import { Badge } from "../components/badge";
import { Button } from "../components/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "../components/command";
import { Popover, PopoverContent, PopoverTrigger } from "../components/popover";
import { Separator } from "../components/separator";
import { cn } from "../lib/utils";
import type { Option } from "./types";

interface DataTableFacetedFilterProps<TData, TValue> {
	column?: Column<TData, TValue>;
	title?: string;
	options: Option[];
	multiple?: boolean;
}

export function DataTableFacetedFilter<TData, TValue>({
	column,
	title,
	options,
	multiple,
}: DataTableFacetedFilterProps<TData, TValue>) {
	// Reads filter state off the mutable column instance. See DataTable.
	"use no memo";

	const [open, setOpen] = React.useState(false);

	const columnFilterValue = column?.getFilterValue();
	const selectedValues = new Set(
		Array.isArray(columnFilterValue) ? columnFilterValue : [],
	);

	const onItemSelect = React.useCallback(
		(option: Option, isSelected: boolean) => {
			if (!column) return;

			if (multiple) {
				const newSelectedValues = new Set(selectedValues);
				if (isSelected) {
					newSelectedValues.delete(option.value);
				} else {
					newSelectedValues.add(option.value);
				}
				const filterValues = Array.from(newSelectedValues);
				column.setFilterValue(filterValues.length ? filterValues : undefined);
			} else {
				column.setFilterValue(isSelected ? undefined : [option.value]);
				setOpen(false);
			}
		},
		[column, multiple, selectedValues],
	);

	const onReset = React.useCallback(() => {
		column?.setFilterValue(undefined);
	}, [column]);

	// Options keep their given order; the groups are only the headings that
	// order already implies. A filter whose options carry no group comes out as
	// a single unlabelled bucket, which renders exactly as a flat list did.
	const groups = React.useMemo(() => {
		const buckets = new Map<string, Option[]>();

		for (const option of options) {
			const heading = option.group ?? "";
			const bucket = buckets.get(heading);
			if (bucket) {
				bucket.push(option);
			} else {
				buckets.set(heading, [option]);
			}
		}

		return Array.from(buckets, ([heading, items]) => ({ heading, items }));
	}, [options]);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			{/* `asChild` is load-bearing: without it PopoverTrigger renders its own
			    button around this one, and a button inside a button is invalid markup
			    that React refuses to hydrate — which took out every table using this
			    filter. The active state is shown with an icon swap rather than a
			    nested clear button for the same reason; clearing lives on the
			    toolbar's Reset and on "Clear filters" inside the popover. */}
			<PopoverTrigger asChild>
				<Button variant="outline" size="sm" className="border-dashed">
					{selectedValues?.size > 0 ? (
						<XCircle aria-hidden="true" />
					) : (
						<PlusCircle aria-hidden="true" />
					)}
					{title}
					{selectedValues?.size > 0 && (
						<>
							<Separator
								orientation="vertical"
								className="mx-0.5 data-[orientation=vertical]:h-4"
							/>
							<Badge
								variant="secondary"
								className="rounded-sm px-1 font-normal lg:hidden"
							>
								{selectedValues.size}
							</Badge>
							<div className="hidden items-center gap-1 lg:flex">
								{selectedValues.size > 2 ? (
									<Badge
										variant="secondary"
										className="rounded-sm px-1 font-normal"
									>
										{selectedValues.size} selected
									</Badge>
								) : (
									options
										.filter((option) => selectedValues.has(option.value))
										.map((option) => (
											// Capped: two selected labels as long as "Password reset
											// requested" would push the rest of the toolbar off the row.
											<Badge
												variant="secondary"
												key={option.value}
												className="max-w-32 rounded-sm px-1 font-normal"
											>
												<span className="truncate">{option.label}</span>
											</Badge>
										))
								)}
							</div>
						</>
					)}
				</Button>
			</PopoverTrigger>
			{/* Sized by its content rather than pinned: option labels are written
			    for the reader, not to fit a box, and "Password reset requested"
			    truncated to "Password r…" is indistinguishable from "Password
			    reset". The floor keeps short lists from looking cramped and the
			    ceiling keeps a long one from covering the table it filters. */}
			<PopoverContent
				className="w-auto min-w-56 max-w-[min(24rem,var(--radix-popover-content-available-width))] p-0"
				align="start"
			>
				<Command>
					<CommandInput placeholder={title} />
					{/* One scroll owner. The list used to be unbounded with the group
					    inside it scrolling, which put a scrollbar in the middle of the
					    popover instead of at its edge. */}
					<CommandList>
						<CommandEmpty>No results found.</CommandEmpty>
						{groups.map((group) => (
							<CommandGroup
								key={group.heading}
								heading={group.heading || undefined}
							>
								{group.items.map((option) => {
									const isSelected = selectedValues.has(option.value);

									return (
										<CommandItem
											key={option.value}
											onSelect={() => onItemSelect(option, isSelected)}
										>
											<div
												className={cn(
													"flex size-4 items-center justify-center rounded-sm border border-primary",
													isSelected
														? "bg-primary"
														: "opacity-50 [&_svg]:invisible",
												)}
											>
												<Check />
											</div>
											{option.icon && <option.icon />}
											{/* Truncation is the last resort rather than the norm now,
											    so the tooltip only has to cover the extremes. */}
											<span className="truncate" title={option.label}>
												{option.label}
											</span>
											{option.count && (
												<span className="ml-auto font-mono text-xs">
													{option.count}
												</span>
											)}
										</CommandItem>
									);
								})}
							</CommandGroup>
						))}
						{selectedValues.size > 0 && (
							<>
								<CommandSeparator />
								<CommandGroup>
									<CommandItem
										onSelect={onReset}
										className="justify-center text-center"
									>
										Clear filters
									</CommandItem>
								</CommandGroup>
							</>
						)}
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
